// Package imports:
import React, { useEffect, useState } from "react";
import Bugsnag from "@bugsnag/js";
// Component imports:
import Modal from "../../ui-elements/Modal/Modal";
// Service imports:
import { ErrorMessages } from "../../services/errorMessages";
// Type imports:
import { ContactsOverview, IDueDiligenceModalProps, KnowYourCustomer, KnowYourIndividual, ServiceCompanyEmployee } from "../../types/DueDiligenceTypes";
import { Fetched } from "../../types/Types";
import Button from "../../ui-elements/Button/Button";
import Table from "../../ui-elements/Table/Table";
import Link from "../../ui-elements/Link/Link";
import ProgressBar from "../../ui-elements/ProgressBar/ProgressBar";
import EditContactModal from "./EditContactModal/EditContactModal";
import EditIndividualModal from "./EditIndividualModal";

interface IProps extends IDueDiligenceModalProps {
    customers?: Fetched<KnowYourCustomer[]>;
    setCustomers?: React.Dispatch<React.SetStateAction<Fetched<KnowYourCustomer[]>>>;
    individuals?: Fetched<KnowYourIndividual[]>;
    setIndividuals?: React.Dispatch<React.SetStateAction<Fetched<KnowYourIndividual[]>>>;
    employees: ServiceCompanyEmployee[];
}

const SendNotificationModal: React.FC<IProps> = ({
    show,
    close,
    customers,
    setCustomers,
    individuals,
    setIndividuals,
    setToastShow,
    setToastType,
    setToastMessage,
    employees
}) => {
    const [disableSubmit, setDisableSubmit] = React.useState(false);
    const [checkedCustomers, setCheckedCustomers] = React.useState<KnowYourCustomer[]>([]);
    const [failedCustomerNotifications, setFailedCustomerNotifications] = React.useState<{ customer: KnowYourCustomer; reason: string; }[] | null>(null);
    const [checkedIndividuals, setCheckedIndividuals] = React.useState<KnowYourIndividual[]>([]);
    const [failedIndividualNotifications, setFailedIndividualNotifications] = React.useState<{ individual: KnowYourIndividual; reason: string; }[] | null>(null);
    const [progress, setProgress] = React.useState<number | null>(null);
    const [animationFinished, setAnimationFinished] = useState(false);

    const [editContactModal_isOpen, setEditContactModal_isOpen] = useState(false);
    const [editContactModal_data, setEditContactModal_data] = useState<{ customerId: number; companyName: string; confirmProcurations: boolean; supervisorWebUserId?: number | null; contactEmail?: string | null }>({ customerId: 0, companyName: '', confirmProcurations: false, supervisorWebUserId: null, contactEmail: null });
    const [editContactModal_contacts, setEditContactModal_contacts] = useState<ContactsOverview | null>(null);

    const [editIndividualModal_isOpen, setEditIndividualModal_isOpen] = useState(false);
    const [editIndividualModal_data, setEditIndividualModal_data] = useState<KnowYourIndividual | null>(null);

    const ANIMATION_DURATION_MS = 300;

    useEffect(() => {
        if (customers instanceof Error || !customers) {
            setCheckedCustomers([]);
            return;
        }
        setCheckedCustomers(customers?.filter(c => c.checked));
    }, [customers]);

    useEffect(() => {
        if (individuals instanceof Error || !individuals) {
            setCheckedIndividuals([]);
            return;
        }
        setCheckedIndividuals(individuals?.filter(i => i.checked));
    }, [individuals]);

    useEffect(() => {
        setFailedCustomerNotifications(null);
        setFailedIndividualNotifications(null);
        setProgress(null);
        setDisableSubmit(false);
        setAnimationFinished(false);
    }, [show]);

    const fetchContacts = async (customerId: number) => {
        try {
            const response = await fetch(`/DueDiligence/GetKycContacts/${customerId}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });

            if (!response.ok) {
                setToastType("error");
                setToastMessage("Ekki tókst að sækja tengiliði");
                setToastShow(true);
            }

            const data = await response.json();
            return data;
        } catch (err) {
            if (err instanceof Error) Bugsnag.notify(err);
            setToastType("error");
            setToastMessage("Ekki tókst að sækja tengiliði");
            setToastShow(true);
        }
    }

    async function processInBatches<T>(
        items: T[],
        batchSize: number,
        handler: (item: T) => Promise<void>
    ) {
        for (let i = 0; i < items.length; i += batchSize) {
            const batch = items.slice(i, i + batchSize);
            await Promise.all(batch.map(handler));

        }
    }

    const handleSubmit = async () => {
        if (customers instanceof Error || individuals instanceof Error) {
            return;
        }
        setDisableSubmit(true);
        setProgress(0);

        const failedCustomers: { customer: KnowYourCustomer; reason: string; }[] = [];
        const successfulCustomers: KnowYourCustomer[] = [];

        const sendCustomerNotification = async (c: KnowYourCustomer) => {
            try {
                const response = await fetch(`/DueDiligence/SendNotification?customerId=${c.customerId}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' }
                });

                if (!response.ok) {
                    const errorData = await response.text();
                    failedCustomers.push({ customer: c, reason: errorData || 'Ekki tókst að senda áreiðanleikakönnun' });
                    setProgress((prev) => (prev !== null ? prev + 1 : 1));
                    return;
                }

                const responseData = await response.json();

                c = {
                    ...c,
                    questionDateSent: responseData.date,
                    status: responseData.status,
                    statusDescription: responseData.statusDescription
                }
                successfulCustomers.push(c);
            } catch (err) {
                if (err instanceof Error) Bugsnag.notify(err);
                failedCustomers.push({ customer: c, reason: ErrorMessages.OtherShort });
            }
            setProgress((prev) => (prev !== null ? prev + 1 : 1));
        };

        const failedIndividuals: { individual: KnowYourIndividual; reason: string; }[] = [];
        const successfulIndividuals: KnowYourIndividual[] = [];

        const sendIndividualNotification = async (i: KnowYourIndividual) => {
            try {
                const response = await fetch(`/DueDiligence/SendIndividualNotification?individualId=${i.individualCustomerId}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' }
                });

                if (!response.ok) {
                    const errorData = await response.text();
                    failedIndividuals.push({ individual: i, reason: errorData || 'Ekki tókst að senda áreiðanleikakönnun' });
                    setProgress((prev) => (prev !== null ? prev + 1 : 1));
                    return;
                }

                const responseData = await response.json();

                i = {
                    ...i,
                    questionDateSent: responseData.date,
                    status: responseData.status,
                    statusDescription: responseData.statusDescription
                }
                successfulIndividuals.push(i);
            } catch (err) {
                if (err instanceof Error) Bugsnag.notify(err);
                failedIndividuals.push({ individual: i, reason: ErrorMessages.OtherShort });
            }
            setProgress((prev) => (prev !== null ? prev + 1 : 1));
        };

        if (customers && setCustomers) {
            await processInBatches(checkedCustomers, 20, sendCustomerNotification);

            // Update customers state with successful notifications
            setCustomers((prevCustomers) => {
                if (prevCustomers instanceof Error || !prevCustomers) {
                    return prevCustomers;
                }
                const updatedCustomers = prevCustomers.map(cust => {
                    const updated = successfulCustomers.find(s => s.customerId === cust.customerId);
                    return updated ? updated : cust;
                });
                return updatedCustomers;
            });
        }

        if (individuals && setIndividuals) {
            await processInBatches(checkedIndividuals, 20, sendIndividualNotification);

            // Update individuals state with successful notifications
            setIndividuals((prevIndividuals) => {
                if (prevIndividuals instanceof Error || !prevIndividuals) {
                    return prevIndividuals;
                }
                const updatedIndividuals = prevIndividuals.map(ind => {
                    const updated = successfulIndividuals.find(s => s.individualCustomerId === ind.individualCustomerId);
                    return updated ? updated : ind;
                });
                return updatedIndividuals;
            });
        }

        setTimeout(() => {
            setAnimationFinished(true);
        }, ANIMATION_DURATION_MS + 300); // Allow animation to finish for more satisfying UX

        setFailedCustomerNotifications(failedCustomers);
        setFailedIndividualNotifications(failedIndividuals);
        setDisableSubmit(false);
    }

    const renderBody = () => (
        <div className="KCL_send-notification-modal">
            {failedCustomerNotifications === null && failedIndividualNotifications === null && progress === null &&
                // Initial state
                <div>
                    <p>
                        Þú ert að fara að senda áreiðanleikakönnun á {customers ? <span className="paragraph--bold">{checkedCustomers.length} lögaðila</span> : <span className="paragraph--bold">{checkedIndividuals.length} einstaklinga</span>}. Tengiliður fær tölvupóst með hlekk á könnun, þar sem hann skráir sig inn með rafrænum skilríkjum og svarar.
                    </p>
                    <p>
                        Viltu halda áfram?
                    </p>
                    <div className="form__actions">
                        <Button
                            buttonType="secondary"
                            size="lg"
                            onClick={close}
                        >
                            Hætta við
                        </Button>
                        <Button
                            size="lg"
                            onClick={handleSubmit}
                            disabled={disableSubmit}
                        >
                            Staðfesta
                        </Button>
                    </div>
                </div>
            }
            {progress !== null && !animationFinished &&
                <div>
                    {customers &&
                        <p>
                            Verið er að senda áreiðanleikakönnun á {checkedCustomers.length} lögaðila.
                        </p>
                    }
                    {individuals &&
                        <p>
                            Verið er að senda áreiðanleikakönnun á {checkedIndividuals.length} einstaklinga.
                        </p>
                    }
                    <ProgressBar size='large' showCount showPercentage={false} animationDuration={ANIMATION_DURATION_MS} current={progress} total={customers ? checkedCustomers.length : checkedIndividuals.length} />
                </div>
            }
            {(failedCustomerNotifications !== null || failedIndividualNotifications !== null) && (customers ? progress === checkedCustomers.length : progress === checkedIndividuals.length) && animationFinished &&
                // Notifications sent state
                <div>
                    {customers && failedCustomerNotifications &&
                        <>
                            <p>
                                Áreiðanleikakönnun hefur verið send á {checkedCustomers.length - failedCustomerNotifications?.length} lögaðila.
                            </p>
                            {failedCustomerNotifications.length > 0 &&
                                <>
                                    <p className="paragraph--bold">
                                        Ekki tókst að senda áreiðanleikakönnun á {failedCustomerNotifications.length} lögaðila:
                                    </p>
                                    <Table
                                        data={failedCustomerNotifications}
                                        columns={[{
                                            title: 'Nafn',
                                            renderCell: ({ customer }) => (
                                                customer.companyName
                                            ),
                                            textAlign: 'left'
                                        }, {
                                            title: 'Kennitala',
                                            renderCell: ({ customer }) => customer.companySSN ? customer.companySSN.replace(/(\d{6})(\d{4})/, '$1-$2') : '999999-9999',
                                            textAlign: 'left'
                                        }, {
                                            title: 'Ástæða',
                                            renderCell: ({ customer, reason }) => {
                                                return <p>{reason} <Link onClick={async () => {
                                                    const contacts = await fetchContacts(customer.customerId);
                                                    setEditContactModal_data({ customerId: customer.customerId, companyName: customer.companyName, confirmProcurations: customer.confirmProcurations, supervisorWebUserId: (customer as any).supervisorWebUserId ?? null, contactEmail: customer.contactEmail ?? null });
                                                    setEditContactModal_contacts(contacts ?? []);
                                                    setEditContactModal_isOpen(true);
                                                }}>Uppfæra tengilið</Link></p>;
                                            },
                                            textAlign: 'left'
                                        }]}
                                    />
                                </>
                            }
                        </>
                    }
                    {individuals && failedIndividualNotifications &&
                        <>
                            <p>
                                Áreiðanleikakönnun hefur verið send á {checkedIndividuals.length - failedIndividualNotifications.length} einstaklinga.
                            </p>
                            {failedIndividualNotifications.length > 0 &&
                                <>
                                    <p className="paragraph--bold">
                                        Ekki tókst að senda áreiðanleikakönnun á {failedIndividualNotifications.length} einstaklinga:
                                    </p>
                                    <Table
                                        data={failedIndividualNotifications}
                                        columns={[{
                                            title: 'Nafn',
                                            renderCell: ({ individual }) => (
                                                individual.name
                                            ),
                                            textAlign: 'left'
                                        }, {
                                            title: 'Kennitala',
                                            renderCell: ({ individual }) => individual.ssn ? individual.ssn.replace(/(\d{6})(\d{4})/, '$1-$2') : '999999-9999',
                                            textAlign: 'left'
                                        }, {
                                            title: 'Ástæða',
                                            renderCell: ({ individual, reason }) => {
                                                return <p>{reason} <Link onClick={async () => {
                                                    setEditIndividualModal_data(individual);
                                                    setEditIndividualModal_isOpen(true);
                                                }}>Uppfæra einstakling</Link></p>;
                                            },
                                            textAlign: 'left'
                                        }]}
                                    />
                                </>
                            }
                        </>
                    }
                    <div className="form__actions">
                        <Button
                            size="lg"
                            onClick={close}
                        >
                            Loka
                        </Button>
                    </div>
                </div>
            }
        </div>
    )

    return (
        <>
            <Modal
                show={show}
                onHide={close}
                header={'Senda könnun'}
                body={renderBody()}
                hasCloseButton
            />
            {customers && setCustomers && editContactModal_isOpen && (
                <EditContactModal
                    show={editContactModal_isOpen}
                    close={() => setEditContactModal_isOpen(false)}
                    data={editContactModal_data}
                    contacts={editContactModal_contacts}
                    setToastShow={setToastShow}
                    setToastType={setToastType}
                    setToastMessage={setToastMessage}
                    customers={customers}
                    setCustomers={setCustomers}
                    employees={employees}
                />
            )}
            {individuals && setIndividuals && editIndividualModal_isOpen && (
                <EditIndividualModal
                    show={editIndividualModal_isOpen}
                    employees={employees}
                    close={() => setEditIndividualModal_isOpen(false)}
                    data={editIndividualModal_data}
                    setToastShow={setToastShow}
                    setToastType={setToastType}
                    setToastMessage={setToastMessage}
                    individuals={individuals}
                    setIndividuals={setIndividuals}
                />
            )}
        </>
    )
}

export default SendNotificationModal;